/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */

/**
 * EmbeddingsGroup.jsx
 *
 * Handles all 3D objects (points, lines, curve, labels) for the embedding visualization.
 * Adds and updates Three.js objects in the scene based on embedding data and display parameters.
 *
 * Author: [Your Name]
 * Date: [Update as needed]
 */
// src/components/EmbeddingsGroup.jsx

import React, { useEffect, useRef } from 'react';
import { DisplayParamTokenColorMode, DisplayParamFogColorMode, DisplayParamEnvironmentColorMode, DisplayParamLabelColorMode, DisplayParamColors } from '../constants';
import * as THREE from 'three';
import CustomPointHelper from '../objects/CustomPointHelper';
import { Text } from 'troika-three-text';

type PointStyleType = 'circle' | 'ring' | 'cloud';

type ChunkLike = {
  chunkEmbeddingReduced?: { fullEmbedding?: number[] };
  chunkTokens?: Array<{ id?: number; text?: string }>;
  chunkLabel?: string;
  sourceTextId?: string;
};
type ColorHelpers = {
  getTokenColor?: (id: number) => string;
  getSequenceColor?: (index: number, total: number) => string;
  getSourceColor?: (sourceTextId?: string) => string;
};

// Example using Google Fonts CDN (Roboto Regular)
const robotoFontUrl = 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxP.ttf';

/**
 * Recursively dispose all Three.js geometries and materials in an object subtree,
 * then remove the object from its parent.
 * @param {THREE.Object3D} obj
 */
function disposeObject(obj: THREE.Object3D) {
  obj.traverse((child: THREE.Object3D) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    if (mesh.material) {
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach(mat => mat.dispose && mat.dispose());
      } else {
        mesh.material.dispose && mesh.material.dispose();
      }
    }
  });
  if (obj.parent) obj.parent.remove(obj);
}

/**
 * Resolve the hex color for a single chunk given the current color mode.
 *
 * @param {object} chunk
 * @param {number} index - Position in the chunk array
 * @param {number} total - Total number of chunks in the array
 * @param {string} colorMode
 * @param {{ getTokenColor: Function, getSequenceColor: Function, getSourceColor: Function }} helpers
 * @returns {string} hex color
 */
function resolveChunkColor(
  chunk: ChunkLike,
  index: number,
  total: number,
  colorMode: string,
  { getTokenColor, getSequenceColor, getSourceColor }: ColorHelpers
): string {
  if (colorMode === DisplayParamTokenColorMode.TOKEN_ID) {
    return getTokenColor(chunk.chunkTokens?.[0]?.id);
  }
  if (colorMode === DisplayParamTokenColorMode.SEQUENCE) {
    return getSequenceColor(index, total);
  }
  if (colorMode === DisplayParamTokenColorMode.SOURCE) {
    return getSourceColor ? getSourceColor(chunk.sourceTextId) : DisplayParamColors.GREY;
  }
  if (
    colorMode === DisplayParamTokenColorMode.BLACK ||
    colorMode === DisplayParamTokenColorMode.WHITE
  ) {
    return DisplayParamColors[colorMode.toUpperCase()] || DisplayParamColors.GREY;
  }
  if (colorMode === DisplayParamTokenColorMode.NONE) {
    return DisplayParamColors.RED;
  }
  return DisplayParamColors.GREY;
}

/**
 * Build all 3D objects (points, lines, curve, labels) for a single array of chunks
 * and add them to the given Three.js parent group.
 *
 * @param {THREE.Group} parent
 * @param {object[]} chunks
 * @param {object} params - Display parameters and color helpers
 */
function buildChunkObjects(
  parent: THREE.Group,
  chunks: ChunkLike[],
  params: {
    displayParam_tokenColorMode: string;
    displayParam_labelColorMode: string;
    showLabels: boolean;
    textSize: number;
    dotSize: number;
    labelOffset: number;
    showPathMode: string;
    pointStyle: PointStyleType;
    getTokenColor?: (id: number) => string;
    getSequenceColor?: (index: number, total: number) => string;
    getSourceColor?: (sourceTextId?: string) => string;
  }
) {
  const {
    displayParam_tokenColorMode,
    displayParam_labelColorMode,
    showLabels,
    textSize,
    dotSize,
    labelOffset,
    showPathMode,
    pointStyle,
    getTokenColor,
    getSequenceColor,
    getSourceColor,
  } = params;

  const colorHelpers = { getTokenColor, getSequenceColor, getSourceColor };

  // Points
  if (chunks.length > 0) {
    const points = chunks.map((chunk, i) => {
      const pca = chunk.chunkEmbeddingReduced?.fullEmbedding;
      if (!pca) return null;
      return {
        position: [pca[0], pca[1], pca[2]] as [number, number, number],
        color: resolveChunkColor(chunk, i, chunks.length, displayParam_tokenColorMode, colorHelpers),
      };
    }).filter((point): point is { position: [number, number, number]; color: string } => point !== null);

    const spriteSize = dotSize * 0.05;
    const pointsObj = new CustomPointHelper(points, { size: spriteSize, pointStyle });
    parent.add(pointsObj);
  }

  // Lines (straight segments)
  if (chunks.length > 1) {
    const linePositions = [];
    const lineColors = [];
    chunks.forEach((chunk, i) => {
      const pca = chunk.chunkEmbeddingReduced?.fullEmbedding;
      const nextPca = chunks[i + 1]?.chunkEmbeddingReduced?.fullEmbedding;
      if (!pca || !nextPca) return;
      const c = new THREE.Color(resolveChunkColor(chunk, i, chunks.length, displayParam_tokenColorMode, colorHelpers));
      linePositions.push(pca[0], pca[1], pca[2], nextPca[0], nextPca[1], nextPca[2]);
      lineColors.push(c.r, c.g, c.b, c.r, c.g, c.b);
    });

    if (linePositions.length > 0) {
      const lineGeom = new THREE.BufferGeometry();
      lineGeom.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
      lineGeom.setAttribute('color', new THREE.Float32BufferAttribute(lineColors, 3));
      const lineMat = new THREE.LineBasicMaterial({
        vertexColors: true,
        opacity: 0.7,
        transparent: true,
        side: THREE.DoubleSide,
      });
      const linesObj = new THREE.LineSegments(lineGeom, lineMat);
      linesObj.visible = (showPathMode === 'lines');
      parent.add(linesObj);
    }
  }

  // Curve (Catmull-Rom spline)
  if (chunks.length > 1) {
    const curvePoints = chunks
      .map(chunk => chunk.chunkEmbeddingReduced?.fullEmbedding)
      .filter(Boolean)
      .map(pca => new THREE.Vector3(pca[0], pca[1], pca[2]));

    if (curvePoints.length > 1) {
      const curve = new THREE.CatmullRomCurve3(curvePoints, false, 'catmullrom', 0.35);
      const numCurvePoints = Math.max(2, curvePoints.length * 10);
      const splinePoints = curve.getPoints(numCurvePoints);
      const splineGeometry = new THREE.BufferGeometry().setFromPoints(splinePoints);
      const splineColors = [];
      for (let i = 0; i < splinePoints.length; i++) {
        const chunkIdx = Math.round(i * (chunks.length - 1) / (splinePoints.length - 1));
        const c = new THREE.Color(resolveChunkColor(chunks[chunkIdx], chunkIdx, chunks.length, displayParam_tokenColorMode, colorHelpers));
        splineColors.push(c.r, c.g, c.b);
      }
      splineGeometry.setAttribute('color', new THREE.Float32BufferAttribute(splineColors, 3));
      const splineMaterial = new THREE.LineBasicMaterial({
        vertexColors: true,
        opacity: 0.7,
        transparent: true,
        linewidth: 1,
        blending: THREE.NormalBlending,
        depthWrite: true,
        depthTest: true,
        side: THREE.DoubleSide,
      });
      const splineLine = new THREE.Line(splineGeometry, splineMaterial);
      splineLine.visible = (showPathMode === 'curve');
      parent.add(splineLine);
    }
  }

  // Labels
  if (showLabels) {
    chunks.forEach((chunk, i) => {
      const pca = chunk.chunkEmbeddingReduced?.fullEmbedding;
      if (!pca) return;
      const firstToken = Array.isArray(chunk.chunkTokens) && chunk.chunkTokens.length > 0
        ? chunk.chunkTokens[0]?.text || ''
        : (chunk.chunkLabel || '');
      const labelColor = resolveChunkColor(chunk, i, chunks.length, displayParam_labelColorMode, colorHelpers);
      const c = new THREE.Color(labelColor);
      const textMesh = new Text();
      textMesh.text = firstToken;
      textMesh.fontSize = textSize * 0.0125;
      textMesh.font = robotoFontUrl;
      textMesh.color = c.getStyle();
      textMesh.anchorX = 'center';
      textMesh.anchorY = 'bottom';
      textMesh.position.set(pca[0], (pca[1] + labelOffset) - (textMesh.fontSize / 1.5), pca[2]);
      textMesh.frustumCulled = false;
      textMesh.userData.isLabel = true;
      textMesh.userData.chunkIndex = i;
      const prevOnBeforeRender = textMesh.onBeforeRender;
      textMesh.onBeforeRender = function(renderer, scene, camera, ...args) {
        if (typeof prevOnBeforeRender === 'function') {
          prevOnBeforeRender.call(this, renderer, scene, camera, ...args);
        }
        const lookTarget = new THREE.Vector3();
        if (camera.isArrayCamera && camera.cameras.length >= 2) {
          const left = new THREE.Vector3();
          const right = new THREE.Vector3();
          camera.cameras[0].getWorldPosition(left);
          camera.cameras[1].getWorldPosition(right);
          lookTarget.copy(left).add(right).multiplyScalar(0.5);
        } else {
          camera.getWorldPosition(lookTarget);
        }
        this.up.set(0, 1, 0);
        this.lookAt(lookTarget);
      };
      parent.add(textMesh);
      textMesh.sync();
    });
  }
}

/**
 * EmbeddingsGroup
 *
 * Imperative Three.js component for rendering all 3D objects (points, lines, curve, labels).
 *
 * @component
 * @param {Object} props
 * @param {Object} props.sceneRef - Ref to the Three.js scene
 * @param {Object} [props.threeSceneManagerRef] - Ref to ThreeSceneManager
 * @param {Array} [props.enrichedChunks] - Array of enriched chunk data (single-group mode)
 * @param {Array} [props.groups] - Array of { id, chunks, color? } for multi-group mode
 * @param {number} [props.labelOffset] - Vertical offset for labels
 * @param {function} [props.getTokenColor] - Color by token id
 * @param {function} [props.getSequenceColor] - Color by sequence index
 * @param {function} [props.getSourceColor] - Color by source text id
 * @param {string} [props.displayParam_tokenColorMode]
 * @param {string} [props.displayParam_labelColorMode]
 * @param {string} [props.displayParam_fogColorMode]
 * @param {string} [props.displayParam_environmentColorMode]
 * @param {boolean} [props.showLabels]
 * @param {number} [props.textSize]
 * @param {number} [props.dotSize]
 * @param {string} [props.pointStyle]
 * @param {string} [props.showPathMode]
 * @returns {null}
 */
type EmbeddingsGroupProps = {
  sceneRef: React.RefObject<THREE.Scene | null>;
  threeSceneManagerRef?: React.RefObject<{ updateAxesHelper_color?: (x: { startColor?: string; endColor?: string }) => void } | null>;
  enrichedChunks?: ChunkLike[];
  groups?: Array<{ id: string; chunks: ChunkLike[] }>;
  displayParam_tokenColorMode?: string;
  displayParam_fogColorMode?: string;
  displayParam_environmentColorMode?: string;
  displayParam_labelColorMode?: string;
  showLabels?: boolean;
  textSize?: number;
  dotSize?: number;
  labelOffset?: number;
  showPathMode?: string;
  getTokenColor?: (id: number) => string;
  getSequenceColor?: (index: number, total: number) => string;
  getSourceColor?: (sourceTextId?: string) => string;
  pointStyle?: PointStyleType;
};

function EmbeddingsGroup({
  sceneRef,
  threeSceneManagerRef,
  enrichedChunks = [],
  groups = [],
  displayParam_tokenColorMode = DisplayParamTokenColorMode.SEQUENCE,
  displayParam_fogColorMode = DisplayParamFogColorMode.NONE,
  displayParam_environmentColorMode = DisplayParamEnvironmentColorMode.BLACK,
  displayParam_labelColorMode = DisplayParamLabelColorMode.SEQUENCE,
  showLabels = true,
  textSize = 2,
  dotSize = 1,
  labelOffset = 0.0,
  showPathMode = 'curve',
  getTokenColor,
  getSequenceColor,
  getSourceColor,
  pointStyle = 'circle',
}: EmbeddingsGroupProps) {
  const groupRef = useRef<THREE.Group | null>(null);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) {
      console.warn('[EmbeddingsGroup][useEffect] No scene found in sceneRef');
      return;
    }

    // --- FOG COLOR HANDLING ---
    if (displayParam_fogColorMode === DisplayParamFogColorMode.NONE) {
      scene.fog = null;
    } else {
      const fogColor = DisplayParamColors[displayParam_fogColorMode.toUpperCase()] || DisplayParamColors.GREY;
      scene.fog = new THREE.Fog(fogColor, 2, 5);
    }

    // --- ENVIRONMENT COLOR HANDLING ---
    const envColor = DisplayParamColors[displayParam_environmentColorMode.toUpperCase()] || DisplayParamColors.GREY;
    scene.background = new THREE.Color(envColor);

    // --- AXES HELPER UPDATE ---
    if (threeSceneManagerRef && threeSceneManagerRef.current && typeof threeSceneManagerRef.current.updateAxesHelper_color === 'function') {
      let startColor: string = DisplayParamColors.WHITE;
      let endColor: string = DisplayParamColors.BLACK;
      if (displayParam_environmentColorMode === DisplayParamEnvironmentColorMode.WHITE) {
        startColor = DisplayParamColors.BLACK;
        endColor = DisplayParamColors.WHITE;
      } else if (displayParam_environmentColorMode === DisplayParamEnvironmentColorMode.DARK_BLUE) {
        startColor = DisplayParamColors.WHITE;
        endColor = DisplayParamColors.DARK_BLUE;
      }
      threeSceneManagerRef.current.updateAxesHelper_color({ startColor, endColor });
    }

    // --- GROUP SETUP ---
    if (!groupRef.current) {
      const group = new THREE.Group();
      group.name = 'embeddingsGroup';
      scene.add(group);
      groupRef.current = group;
    }
    const group = groupRef.current as THREE.Group;

    // Clear and dispose all existing children
    while (group.children.length > 0) {
      disposeObject(group.children[0]);
    }

    group.position.set(0, 0, 0);
    group.rotation.set(0, 0, 0);
    group.scale.set(1, 1, 1);

    const displayParams = {
      displayParam_tokenColorMode,
      displayParam_labelColorMode,
      showLabels,
      textSize,
      dotSize,
      labelOffset,
      showPathMode,
      pointStyle,
      getTokenColor,
      getSequenceColor,
      getSourceColor,
    };

    if (groups.length > 0) {
      // Multi-group mode: each entry gets its own sub-group
      groups.forEach(({ id, chunks: groupChunks }) => {
        if (!groupChunks || groupChunks.length === 0) return;
        const subGroup = new THREE.Group();
        subGroup.name = `embeddingsSubGroup_${id}`;
        buildChunkObjects(subGroup, groupChunks, displayParams);
        group.add(subGroup);
      });
    } else {
      // Single-group fallback using enrichedChunks
      buildChunkObjects(group, enrichedChunks, displayParams);
    }

    // Re-attach group to scene if needed
    if (group.parent !== scene) {
      if (group.parent) group.parent.remove(group);
      scene.add(group);
    }
  }, [
    sceneRef,
    threeSceneManagerRef,
    enrichedChunks,
    groups,
    displayParam_tokenColorMode,
    displayParam_fogColorMode,
    displayParam_environmentColorMode,
    displayParam_labelColorMode,
    showLabels,
    textSize,
    dotSize,
    labelOffset,
    showPathMode,
    getTokenColor,
    getSequenceColor,
    getSourceColor,
    pointStyle,
  ]);

  return null;
}

export default EmbeddingsGroup;

